import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import EventCard from "./EventCard";
import WelcomeHeader from "./WelcomeHeader";
import { Calendar, Tag, ChevronRight } from "lucide-react";
import { supabase } from "../utils/supabase";

const EventsPage = ({ userProfile = {} }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .order("event_date", { ascending: true });

      if (error) {
        console.error("Error fetching events:", error);
        setError("Error al cargar los eventos");
        setEvents(getExampleEvents());
      } else {
        const transformedEvents = (data || []).map((event) => ({
          id: event.id,
          title: event.title,
          description: event.description,
          image_url:
            event.image_url ||
            "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=800&h=600&fit=crop",
          type: "evento",
          category: "Eventos",
          event_date: event.event_date,
          location: event.location,
          capacity: event.capacity,
          price: event.price,
          is_free: event.is_free || false,
          registration_required: event.registration_required || false,
        }));

        if (transformedEvents.length === 0) {
          setEvents(getExampleEvents());
        } else {
          setEvents(transformedEvents);
        }
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      setError("Error al cargar los eventos");
      setEvents(getExampleEvents());
    } finally {
      setLoading(false);
    }
  };

  const getExampleEvents = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    return [
      {
        id: "example-1",
        title: "Degustación de Vinos Premium",
        description:
          "Únete a nuestra exclusiva degustación de vinos premium seleccionados especialmente para los miembros de Club Mia. Descubre sabores únicos y aprende sobre maridajes perfectos.",
        image_url:
          "https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=800&h=600&fit=crop",
        type: "evento",
        category: "Eventos",
        event_date: tomorrow.toISOString(),
        location: "Supermercados Mia - Sede Principal",
        capacity: 25,
        price: 45,
        is_free: false,
        registration_required: true,
      },
      {
        id: "example-2",
        title: "Clase de Cocina: Platos Gourmet",
        description:
          "Aprende a preparar deliciosos platos gourmet con nuestro chef especializado. Incluye todos los ingredientes y recetas para llevar a casa.",
        image_url:
          "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600&fit=crop",
        type: "evento",
        category: "Eventos",
        event_date: nextWeek.toISOString(),
        location: "Centro Culinario Mia",
        capacity: 15,
        price: null,
        is_free: true,
        registration_required: true,
      },
      {
        id: "example-3",
        title: "Festival de Productos Orgánicos",
        description:
          "Celebra con nosotros el Festival de Productos Orgánicos. Conoce a productores locales, prueba productos frescos y participa en actividades familiares.",
        image_url:
          "https://images.unsplash.com/photo-1542838132-92c53300491e?w=800&h=600&fit=crop",
        type: "evento",
        category: "Eventos",
        event_date: nextMonth.toISOString(),
        location: "Plaza Central Mia",
        capacity: 100,
        price: null,
        is_free: true,
        registration_required: false,
      },
      {
        id: "example-4",
        title: "Taller de Repostería Francesa",
        description:
          "Descubre los secretos de la repostería francesa con nuestro chef pastelero. Aprende técnicas profesionales y crea deliciosos postres.",
        image_url:
          "https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=800&h=600&fit=crop",
        type: "evento",
        category: "Eventos",
        event_date: new Date(
          today.getTime() + 14 * 24 * 60 * 60 * 1000
        ).toISOString(),
        location: "Academia Culinaria Mia",
        capacity: 12,
        price: 80,
        is_free: false,
        registration_required: true,
      },
      {
        id: "example-5",
        title: "Mercado de Productores Locales",
        description:
          "Conoce a los productores locales de nuestra región. Compra productos frescos directamente del campo y apoya la economía local.",
        image_url:
          "https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=800&h=600&fit=crop",
        type: "evento",
        category: "Eventos",
        event_date: new Date(
          today.getTime() + 21 * 24 * 60 * 60 * 1000
        ).toISOString(),
        location: "Estacionamiento Mia Centro",
        capacity: 200,
        price: null,
        is_free: true,
        registration_required: false,
      },
    ];
  };

  const filteredEvents = events.filter((event) => {
    const matchesCategory =
      filterCategory === "all" || event.category === filterCategory;
    return matchesCategory;
  });

  const categories = [
    { value: "all", label: "Recomendación" },
    { value: "Talleres", label: "Talleres de cocina" },
    { value: "Degustaciones", label: "Degustaciones" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando eventos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 pt-24 px-4 pb-8">
      <div className="max-w-7xl mx-auto space-y-8 mt-16">
        {/* Header */}
        {userProfile && Object.keys(userProfile).length > 0 && (
          <WelcomeHeader userProfile={userProfile} title="Disfuta los mejores eventos exclusivos de Club Mia!" />
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mt-6 text-center"
        >
          <button
            onClick={() => navigate("/ofertas")}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white font-semibold rounded-full hover:from-orange-700 hover:to-red-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <Tag className="w-5 h-5 mr-2" />
            Ver Ofertas Exclusivas
            <ChevronRight className="w-5 h-5 ml-2" />
          </button>
        </motion.div>

        {/* Filtros por categoría */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="max-w-4xl mx-auto mb-12"
        >
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => setFilterCategory(category.value)}
                className={`px-6 py-2 rounded-full border transition-all duration-300 ${
                  filterCategory === category.value
                    ? "bg-blue-500 text-white border-blue-500 shadow-lg"
                    : "bg-white text-gray-700 border-gray-300 hover:border-blue-300 hover:bg-blue-50"
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Grid de eventos */}
        {error && events.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center py-12"
          >
            <Calendar className="w-16 h-16 text-red-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-red-600 mb-2">
              Error al cargar eventos
            </h3>
            <p className="text-red-500">{error}</p>
          </motion.div>
        ) : filteredEvents.length > 0 ? (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {filteredEvents.map((event, index) => (
              <EventCard key={event.id} event={event} index={index} />
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center py-12"
          >
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              No hay eventos próximos
            </h3>
            <p className="text-gray-500">
              {filterCategory !== "all"
                ? "No hay eventos en esta categoría. ¡Prueba con otra categoría!"
                : "¡Mantente atento! Pronto tendremos eventos increíbles para ti."}
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
